// ABOUTME: Renders interactive Plotly visualizations for python-viz insights.
// ABOUTME: Wraps plotly.js in card styling matching the Precision Sharp design system.

import React, { useMemo, useEffect, useRef } from 'react';
import Plot from 'react-plotly.js';
import type { PythonVizConfig } from '../../types/sdk';
import { usePlotlyTheme } from '@/hooks/usePlotlyTheme';

/**
 * Deep merge two Plotly layout objects, with source values taking precedence.
 * Arrays are replaced, not merged. Only plain objects are recursively merged.
 */
function deepMergeLayout(
  target: Partial<Plotly.Layout>,
  source: Partial<Plotly.Layout>
): Partial<Plotly.Layout> {
  const result: Partial<Plotly.Layout> = { ...target };

  for (const key of Object.keys(source) as Array<keyof Plotly.Layout>) {
    const sourceValue = source[key];
    const targetValue = target[key];

    if (
      sourceValue !== null &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue !== null &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      // Both are plain objects - recurse
      (result as Record<string, unknown>)[key] = deepMergeLayout(
        targetValue as Partial<Plotly.Layout>,
        sourceValue as Partial<Plotly.Layout>
      );
    } else if (sourceValue !== undefined) {
      // Replace with source value
      (result as Record<string, unknown>)[key] = sourceValue;
    }
  }

  return result;
}

interface PythonVizRendererProps {
  pythonViz: PythonVizConfig;
  className?: string;
}

export const PythonVizRenderer: React.FC<PythonVizRendererProps> = ({
  pythonViz,
  className = '',
}) => {
  const { plotly_spec, aspect_ratio = 1 } = pythonViz;

  // Get theme-aware layout overrides from CSS variables
  // Must be called before any conditional returns to satisfy rules of hooks
  const layoutOverrides = usePlotlyTheme();

  // Defensive null check for plotly_spec
  if (!plotly_spec) {
    return (
      <div className={`w-full card rounded-sm p-4 ${className}`}>
        <p className="text-surface-500">Error: Missing visualization data</p>
      </div>
    );
  }

  const mergedLayout = useMemo((): Partial<Plotly.Layout> => {
    const baseLayout = typeof plotly_spec.layout === 'object' ? plotly_spec.layout : {};
    return deepMergeLayout(baseLayout as Partial<Plotly.Layout>, layoutOverrides);
  }, [plotly_spec.layout, layoutOverrides]);

  const config: Partial<Plotly.Config> = useMemo(() => ({
    responsive: true,
    displayModeBar: 'hover',            // Show toolbar on hover
    scrollZoom: true,                   // Enable scroll-to-zoom
    modeBarButtonsToRemove: ['toImage', 'sendDataToCloud'],  // Remove unnecessary buttons
    displaylogo: false,                 // Hide Plotly logo
  }), []);

  // Calculate height based on aspect ratio
  // overflow: 'hidden' ensures Plotly content stays within bounds during relayout
  const containerStyle = useMemo(() => ({
    width: '100%',
    aspectRatio: aspect_ratio.toString(),
    minHeight: '300px',
    maxHeight: '600px',
    overflow: 'hidden' as const,
  }), [aspect_ratio]);

  // Track if this is the initial render to avoid triggering resize on mount
  const isInitialMount = useRef(true);

  // Force Plotly to recalculate dimensions when theme changes
  // Without this, Plotly updates colors but doesn't recalculate layout positioning
  // because the container size hasn't actually changed - only the layout prop has
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Dispatch resize event after layout changes have been applied
    // This triggers Plotly's useResizeHandler to recalculate dimensions
    const frameId = requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => cancelAnimationFrame(frameId);
  }, [layoutOverrides]);

  return (
    <div
      className={`w-full card rounded-sm p-4 ${className}`}
      style={containerStyle}
    >
      <Plot
        data={plotly_spec.data as Plotly.Data[]}
        layout={mergedLayout}
        config={config}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
      />
    </div>
  );
};

export default PythonVizRenderer;
